import type { IChoiceGroupOption } from "@fluentui/react";
import { Checkbox, ChoiceGroup } from "@fluentui/react";
import React, { useEffect, useState, type FormEvent } from "react";
import type { NodeProps, Node } from "@xyflow/react";
import type { AnyInZoneData } from "../../../api";
import { Zone } from "../../../api";
import ActionConfigNode, {
    zoneOptions,
    type InterfaceToRecord,
    type ExtraNodeData,
} from "./action-config-node";
import { useTranslation } from "react-i18next";
import { useUpdateNodeData } from "@/lib/hooks/reactflow";

type AnyInZoneNode = Node<InterfaceToRecord<AnyInZoneData & ExtraNodeData>>;

export default function AnyInZone(props: NodeProps<AnyInZoneNode>): JSX.Element {
    const { t } = useTranslation();
    const [selectedZone, setSelectedZone] = useState(props.data.zone ?? Zone.ONE);
    const [pedestrian, setPedestrian] = useState(props.data.pedestrian ?? true);
    const [vehicle, setVehicle] = useState(props.data.vehicle ?? true);
    const [station, setStation] = useState(props.data.station ?? true);

    const updateNode = useUpdateNodeData<AnyInZoneNode>(props.id);

    // We have to set the data to prevent undefined values
    useEffect(() => {
        updateNode({ zone: selectedZone, pedestrian, vehicle, station });
    }, [selectedZone, pedestrian, vehicle, station, updateNode]);

    function onChange(
        _e?: FormEvent<HTMLElement | HTMLInputElement>,
        option?: IChoiceGroupOption,
    ): void {
        if (!option) {
            return;
        }
        setSelectedZone(option.key as Zone);
    }

    return (
        <ActionConfigNode nodeKind={props.type} {...props.data}>
            <ChoiceGroup options={zoneOptions(t)} selectedKey={selectedZone} onChange={onChange} />
            <Checkbox
                label={t("component.actionConfigNodes.anyInZone.pedestrian")}
                checked={pedestrian}
                onChange={(_e, checked) => setPedestrian(checked!)}
            />
            <Checkbox
                label={t("component.actionConfigNodes.anyInZone.vehicle")}
                checked={vehicle}
                onChange={(_e, checked) => setVehicle(checked!)}
            />
            <Checkbox
                label={t("component.actionConfigNodes.anyInZone.station")}
                checked={station}
                onChange={(_e, checked) => setStation(checked!)}
            />
        </ActionConfigNode>
    );
}
